import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {
    History,
    Print,
    Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import {
    createPrintWorkflowBatch,
    getPrintWorkflowClients,
    getPrintWorkflowDestinations,
    getPrintWorkflowQueue,
} from '../services/api';
import { useSnackbar } from '../contexts/SnackbarContext';
import { AuthContext } from '../contexts/AuthContext';

const COUNT_OPTIONS = [1, 2, 4, 8, 16, 32, 64, 128];

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(Number(value) || value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('en-GB');
};

const statusColor = (status) => {
    if (status === 'Sent') return 'success';
    if (['Failed', 'Error'].includes(status)) return 'error';
    if (status === 'Needs attention') return 'warning';
    return 'info';
};

const getErrorMessage = (error) => error?.response?.data?.message || error?.message || 'Request failed.';

const BatchPrintJobs = () => {
    const navigate = useNavigate();
    const auth = useContext(AuthContext);
    const { showSnackbar } = useSnackbar();
    const canViewAudit = Boolean(auth?.permissions?.includes('ViewPrintBatchAudit'));
    const [clients, setClients] = useState([]);
    const [clientSlug, setClientSlug] = useState('');
    const [destinations, setDestinations] = useState([]);
    const [destinationSlug, setDestinationSlug] = useState('');
    const [clientsLoading, setClientsLoading] = useState(true);
    const [queue, setQueue] = useState(null);
    const [queueError, setQueueError] = useState('');
    const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selections, setSelections] = useState({});
    const [confirmRow, setConfirmRow] = useState(null);
    const [submittingRowId, setSubmittingRowId] = useState(null);

    const loadClients = useCallback(async () => {
        setClientsLoading(true);
        setQueueError('');
        try {
            const [data, destinationData] = await Promise.all([
                getPrintWorkflowClients(),
                getPrintWorkflowDestinations(),
            ]);
            if (!data || !Array.isArray(data.clients)) {
                throw new Error('The print workflow API returned an invalid client list.');
            }
            const pressList = Array.isArray(destinationData?.destinations) ? destinationData.destinations : [];
            setClients(data.clients);
            setDestinations(pressList);
            setClientSlug((current) => (
                data.clients.some((client) => client.slug === current)
                    ? current
                    : data.clients[0]?.slug || ''
            ));
            setDestinationSlug((current) => (
                pressList.some((destination) => destination.slug === current)
                    ? current
                    : pressList[0]?.slug || ''
            ));
            if (!data.clients.length) {
                setQueueError('No enabled print workflow clients are configured.');
                setLoading(false);
            }
            if (!pressList.length) {
                setQueueError((current) => current || 'No print destinations are configured.');
            }
        } catch (error) {
            const message = getErrorMessage(error);
            setClients([]);
            setClientSlug('');
            setDestinations([]);
            setDestinationSlug('');
            setQueueError(message);
            setLoading(false);
            showSnackbar(message, 'error');
        } finally {
            setClientsLoading(false);
        }
    }, [showSnackbar]);

    const loadQueue = useCallback(async ({ quiet = false } = {}) => {
        if (!clientSlug) {
            setLoading(false);
            return;
        }
        if (quiet) setRefreshing(true);
        else setLoading(true);
        setQueueError('');
        try {
            const data = await getPrintWorkflowQueue(clientSlug);
            if (!data || !Array.isArray(data.rows)) {
                throw new Error('The print workflow API returned an invalid queue response.');
            }
            setQueue(data);
            setLastRefreshedAt(new Date());
            setSelections((current) => {
                const next = { ...current };
                data.rows.forEach((row) => {
                    const selected = next[row.rowId];
                    if (!selected || (selected !== 'all' && Number(selected) > row.readyCount)) {
                        next[row.rowId] = 'all';
                    }
                });
                return next;
            });
        } catch (error) {
            const message = getErrorMessage(error);
            setQueue(null);
            setQueueError(message);
            showSnackbar(message, 'error');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [clientSlug, showSnackbar]);

    useEffect(() => {
        loadClients();
    }, [loadClients]);

    useEffect(() => {
        if (clientSlug) loadQueue();
    }, [clientSlug, loadQueue]);

    const hasRunningBatch = useMemo(
        () => Boolean(queue?.rows?.some((row) => (
            row.activeBatch && ['Sending', 'Reconciling', 'Releasing', 'Force releasing'].includes(row.activeBatch.status)
        ))),
        [queue]
    );
    const hasSettlingFiles = useMemo(
        () => Boolean(queue?.rows?.some((row) => row.settlingCount > 0)),
        [queue]
    );

    useEffect(() => {
        if (!hasRunningBatch && !hasSettlingFiles) return undefined;
        const timer = window.setInterval(() => loadQueue({ quiet: true }), 5000);
        return () => window.clearInterval(timer);
    }, [hasRunningBatch, hasSettlingFiles, loadQueue]);

    const selectedDestination = destinations.find((destination) => destination.slug === destinationSlug) || null;
    const isFieryDestination = selectedDestination?.destinationType === 'fiery';
    const requiresJobSpec = Boolean(selectedDestination) && !isFieryDestination;
    const fieryWorkflowPreset = queue?.client?.fieryWorkflowPreset || '';

    const canSubmitRow = (row) => (
        Boolean(row.readyCount)
        && !row.activeBatch
        && queue?.sftp?.ok
        && Boolean(destinationSlug)
        && (!isFieryDestination || Boolean(fieryWorkflowPreset))
        && (!requiresJobSpec || row.configured)
    );

    const openConfirmation = (row) => {
        if (!canSubmitRow(row)) return;
        setConfirmRow(row);
    };

    const submitBatch = async () => {
        if (!confirmRow) return;
        setSubmittingRowId(confirmRow.rowId);
        try {
            const result = await createPrintWorkflowBatch({
                clientSlug,
                rowId: confirmRow.rowId,
                count: selections[confirmRow.rowId] || 'all',
                destinationSlug,
            });
            showSnackbar(`Batch ${result.batch.batchId} queued for ${confirmRow.name}.`, 'success');
            setConfirmRow(null);
            await loadQueue({ quiet: true });
        } catch (error) {
            showSnackbar(getErrorMessage(error), 'error');
        } finally {
            setSubmittingRowId(null);
        }
    };

    const selectedClientName = queue?.client?.name
        || clients.find((client) => client.slug === clientSlug)?.name
        || 'Client';

    return (
        <Layout>
            <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography variant="h5" fontWeight={750}>Batch Print</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedClientName}: each product row is submitted as one JDF/JMF batch to the selected press. Artwork copy counts come from each PDF filename.
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {lastRefreshedAt
                                    ? `Last refreshed: ${lastRefreshedAt.toLocaleTimeString('en-GB')}`
                                    : 'Loading queue…'}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <FormControl size="small" sx={{ minWidth: 260 }}>
                                <InputLabel id="batch-print-press-label">Press</InputLabel>
                                <Select
                                    labelId="batch-print-press-label"
                                    label="Press"
                                    value={destinationSlug}
                                    disabled={clientsLoading || !destinations.length || refreshing}
                                    onChange={(event) => {
                                        setDestinationSlug(event.target.value);
                                        setConfirmRow(null);
                                    }}
                                >
                                    {destinations.map((destination) => (
                                        <MenuItem key={destination.slug} value={destination.slug}>
                                            {destination.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 190 }}>
                                <InputLabel id="batch-print-client-label">Client</InputLabel>
                                <Select
                                    labelId="batch-print-client-label"
                                    label="Client"
                                    value={clientSlug}
                                    disabled={clientsLoading || !clients.length || refreshing}
                                    onChange={(event) => {
                                        setClientSlug(event.target.value);
                                        setQueue(null);
                                        setSelections({});
                                        setConfirmRow(null);
                                        setLoading(true);
                                    }}
                                >
                                    {clients.map((client) => (
                                        <MenuItem key={client.clientId} value={client.slug}>
                                            {client.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {canViewAudit && (
                                <Button
                                    variant="outlined"
                                    startIcon={<History />}
                                    onClick={() => navigate('/print-batch-audit')}
                                >
                                    Audit history
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
                                onClick={() => loadQueue({ quiet: true })}
                                disabled={refreshing}
                            >
                                {refreshing ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                {queue?.sftp && !queue.sftp.ok && (
                    <Alert severity="error">
                        SFTP queue is unavailable: {queue.sftp.message}
                    </Alert>
                )}
                {queue && isFieryDestination && !fieryWorkflowPreset && (
                    <Alert severity="warning">
                        No Fiery workflow/preset is configured for {selectedClientName}. Add it in Print Workflow Settings before submitting to Fuji/Revoria.
                    </Alert>
                )}
                {queueError && <Alert severity="error">{queueError}</Alert>}

                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    {loading ? (
                        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 260 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Product row</TableCell>
                                        <TableCell align="right">Ready artwork</TableCell>
                                        <TableCell align="right">Total copies</TableCell>
                                        <TableCell>Oldest artwork</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell width={150}>Artwork files</TableCell>
                                        <TableCell align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(queue?.rows || []).map((row) => {
                                        const active = row.activeBatch;
                                        const options = COUNT_OPTIONS.filter((value) => value <= row.readyCount);
                                        return (
                                            <TableRow key={row.rowId} hover>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={700}>{row.name}</Typography>
                                                    {requiresJobSpec && !row.configured && (
                                                        <Typography variant="caption" color="warning.main">
                                                            JobSpec setup required for HP Indigo
                                                        </Typography>
                                                    )}
                                                    {row.invalidCount > 0 && (
                                                        <Typography variant="caption" color="error" display="block">
                                                            {row.invalidCount} invalid PDF filename(s)
                                                            {row.invalidFiles?.length
                                                                ? `: ${row.invalidFiles.join(', ')}`
                                                                : ''}
                                                        </Typography>
                                                    )}
                                                    {row.settlingCount > 0 && (
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            {row.settlingCount} file(s) still settling after a recent upload or rename.
                                                            Wait up to {row.settlingGraceSeconds || 30}s, then Refresh.
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">{row.readyCount}</TableCell>
                                                <TableCell align="right">{row.totalCopies}</TableCell>
                                                <TableCell>{formatDate(row.oldestModifiedTime)}</TableCell>
                                                <TableCell sx={{ minWidth: 180 }}>
                                                    {active ? (
                                                        <Stack spacing={0.5}>
                                                            <Stack direction="row" spacing={0.75} alignItems="center">
                                                                <Chip size="small" label={active.status} color={statusColor(active.status)} />
                                                                <Typography variant="caption">
                                                                    {active.phase} {active.progressCurrent}/{active.progressTotal}
                                                                    {active.destinationName ? ` · ${active.destinationName}` : ''}
                                                                </Typography>
                                                            </Stack>
                                                            <LinearProgress
                                                                variant={active.progressTotal ? 'determinate' : 'indeterminate'}
                                                                value={active.progressTotal
                                                                    ? Math.min(100, (active.progressCurrent / active.progressTotal) * 100)
                                                                    : 0}
                                                            />
                                                        </Stack>
                                                    ) : (
                                                        <Chip size="small" variant="outlined" label="Ready" color="success" />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <FormControl fullWidth size="small">
                                                        <Select
                                                            value={selections[row.rowId] || 'all'}
                                                            onChange={(event) => setSelections((current) => ({
                                                                ...current,
                                                                [row.rowId]: event.target.value,
                                                            }))}
                                                            disabled={!row.readyCount || Boolean(active)}
                                                            inputProps={{ 'aria-label': `${row.name} artwork count` }}
                                                        >
                                                            {options.map((value) => (
                                                                <MenuItem key={value} value={value}>{value}</MenuItem>
                                                            ))}
                                                            <MenuItem value="all">All ({row.readyCount})</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={submittingRowId === row.rowId
                                                            ? <CircularProgress color="inherit" size={15} />
                                                            : <Print />}
                                                        disabled={
                                                            !canSubmitRow(row)
                                                            || submittingRowId === row.rowId
                                                        }
                                                        onClick={() => openConfirmation(row)}
                                                    >
                                                        Batch Print
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {!queue?.rows?.length && (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                                                No enabled print workflow rows are configured.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            </Stack>

            <Dialog open={Boolean(confirmRow)} onClose={() => setConfirmRow(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm send to {selectedDestination?.name || 'press'}</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This creates one JDF/JMF batch and submits it to {selectedDestination?.name || 'the selected press'}. The press operator still starts physical printing.
                    </Alert>
                    <Typography>
                        <strong>{selectedClientName} · {confirmRow?.name}</strong>
                    </Typography>
                    <Typography color="text.secondary">
                        Press: {selectedDestination?.name || '—'}
                    </Typography>
                    {isFieryDestination && (
                        <Typography color="text.secondary">
                            Fiery workflow/preset: {fieryWorkflowPreset || '—'}
                        </Typography>
                    )}
                    <Typography color="text.secondary">
                        Artwork files: {selections[confirmRow?.rowId] === 'all'
                            ? `All (${confirmRow?.readyCount || 0})`
                            : selections[confirmRow?.rowId]}
                    </Typography>
                    <Typography color="text.secondary">
                        Files are selected oldest first. Each PDF keeps the copy quantity encoded in its filename.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmRow(null)} disabled={Boolean(submittingRowId)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={submitBatch}
                        disabled={Boolean(submittingRowId)}
                        startIcon={submittingRowId ? <CircularProgress color="inherit" size={16} /> : <Print />}
                    >
                        Send batch
                    </Button>
                </DialogActions>
            </Dialog>
        </Layout>
    );
};

export default BatchPrintJobs;
